import { useMutation } from "@apollo/client";
import { COMPLETE_MULTIPART_UPLOAD } from "../components/partners/graphql/mutations/completeMultipartUpload";
import { INIT_MULTIPART_UPLOAD } from "../components/partners/graphql/mutations/initMultipartUpload";
import { executeMultipartUpload } from "../utils/multipartUpload";

const UPLOAD_TYPE = "SEGMENT";

const SEGMENT_UPLOAD_ERROR_MESSAGES = {
  ERR_SEGMENT_NOT_FOUND: "Segment not found.",
  ERR_SEGMENT_FILE_UPLOAD_NOT_ELIGIBLE: "This segment cannot accept uploads.",
  ERR_SEGMENT_FILE_UPLOAD_INVALID_FILE:
    "The file must be a .csv file no larger than 500 MB.",
};

/**
 * Maps a pepl-segments error code (passed through by the BFF) to a friendly
 * message. Falls back to the original error message.
 */
const getSegmentUploadErrorMessage = (error) => {
  const candidates = [
    error?.message,
    ...(error?.graphQLErrors || []).flatMap((graphQLError) => [
      graphQLError?.message,
      graphQLError?.extensions?.error_code,
      graphQLError?.extensions?.code,
      graphQLError?.extensions?.errorCode,
    ]),
  ].filter(Boolean);

  const knownCode = Object.keys(SEGMENT_UPLOAD_ERROR_MESSAGES).find((code) =>
    candidates.some((candidate) => String(candidate).includes(code))
  );

  return knownCode
    ? SEGMENT_UPLOAD_ERROR_MESSAGES[knownCode]
    : error?.message || "Something went wrong during file upload";
};

/**
 * Hook for the Platform Segment "Import users" upload.
 *
 * Uses the shared executeMultipartUpload utility (src/utils/multipartUpload.js)
 * with uploadType = "SEGMENT". Status tracking after the upload is handled by
 * the shared FileImportStatus tracker with getSegmentFileUpload.
 */
export function useSegmentUsersUpload() {
  const [initMultipartUploadMutation] = useMutation(INIT_MULTIPART_UPLOAD);
  const [completeMultipartUploadMutation] = useMutation(COMPLETE_MULTIPART_UPLOAD);

  /**
   * @param {File|Blob} file - CSV file to upload
   * @param {string} segmentId - Segment the users are imported into
   * @returns {Object} { status: true, uploadId } once the upload is complete, or { status: false, message }
   */
  const handleUpload = async (file, segmentId) => {
    if (!file || !(file instanceof File || file instanceof Blob)) {
      return { status: false, message: "No valid file provided" };
    }
    if (!segmentId) {
      return { status: false, message: "Segment ID is required" };
    }

    try {
      const { uploadId } = await executeMultipartUpload({
        file,
        entityId: segmentId,
        uploadType: UPLOAD_TYPE,
        initMutation: initMultipartUploadMutation,
        completeMutation: completeMultipartUploadMutation,
        contentType: "text/csv",
      });
      return { status: true, uploadId };
    } catch (error) {
      console.error("Segment users upload error:", error);
      return { status: false, message: getSegmentUploadErrorMessage(error) };
    }
  };

  return { handleUpload };
}
